import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useBranding } from "../../branding";
import { PROBLEM_CODES, api, errorMessage, problemCode, useApiMutation } from "../../api";
import type { Company } from "../../api";
import { Badge, Button, Card, ConfirmDialog, Field, FileInput, Logo, useToast } from "../../components";
import { HEX_COULEUR, LOGO_MAX_BYTES, LOGO_TYPES, accentOverride, contrastRatio } from "../../theme/brand";
import styles from "./Parametres.module.css";

export type MarqueFormulairesProps = {
  company: Company;
  /** Appelé avec la fiche mise à jour après chaque changement réussi. */
  onUpdated: (company: Company) => void;
};

/**
 * Les trois cartes de la marque : nom, logo, couleur. Partagées entre les
 * paramètres d'une entreprise (pour son admin) et sa fiche vue par l'ESN.
 */
export function MarqueFormulaires({ company, onUpdated }: MarqueFormulairesProps) {
  return (
    <div className={styles.marque}>
      <FormulaireNom company={company} onUpdated={onUpdated} />
      <FormulaireLogo company={company} onUpdated={onUpdated} />
      <FormulaireAccent company={company} onUpdated={onUpdated} />
    </div>
  );
}

function FormulaireNom({ company, onUpdated }: MarqueFormulairesProps) {
  const { notify } = useToast();
  const [nom, setNom] = useState(company.name);
  useEffect(() => setNom(company.name), [company.name]);
  const maj = useApiMutation((n: string) => api.updateCompanyBranding(company.id, { name: n }));

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    const c = await maj.run(nom.trim());
    if (c === null) {
      return;
    }
    onUpdated(c);
    notify({ title: "Nom enregistré", tone: "success" });
  }

  const erreur =
    problemCode(maj.error) === PROBLEM_CODES.invalidCompanyName
      ? "Le nom doit compter entre 1 et 120 caractères."
      : maj.error !== null
        ? errorMessage(maj.error)
        : nom.trim() === ""
          ? "Le nom ne peut pas être vide."
          : undefined;

  return (
    <Card title="Nom" subtitle="Affiché dans la navigation et dans les courriels d'invitation.">
      <form className={styles.formulaire} onSubmit={(e) => void soumettre(e)} noValidate>
        <Field label="Nom de l'entreprise" error={erreur} required>
          {(commande) => (
            <input {...commande} type="text" maxLength={120} value={nom} onChange={(e) => setNom(e.target.value)} disabled={maj.pending} />
          )}
        </Field>
        <Button type="submit" variant="primary" loading={maj.pending} disabled={nom.trim() === "" || nom.trim() === company.name}>
          Enregistrer le nom
        </Button>
      </form>
    </Card>
  );
}

function FormulaireLogo({ company, onUpdated }: MarqueFormulairesProps) {
  const { notify } = useToast();
  const [fichier, setFichier] = useState<File | null>(null);
  const [erreurFichier, setErreurFichier] = useState<string | null>(null);
  const [confirmerRetrait, setConfirmerRetrait] = useState(false);
  const envoi = useApiMutation((f: File) => api.uploadCompanyLogo(company.id, f));
  const retrait = useApiMutation(() => api.deleteCompanyLogo(company.id));

  async function enregistrer() {
    if (fichier === null) {
      return;
    }
    const c = await envoi.run(fichier);
    if (c === null) {
      return;
    }
    setFichier(null);
    onUpdated(c);
    notify({ title: "Logo enregistré", tone: "success" });
  }

  async function retirer() {
    const c = await retrait.run();
    if (c === null) {
      notify({ title: "Retrait impossible", description: errorMessage(retrait.error), tone: "danger" });
      return;
    }
    setConfirmerRetrait(false);
    onUpdated(c);
    notify({ title: "Logo retiré", tone: "neutral" });
  }

  const codeEnvoi = problemCode(envoi.error);
  const erreurEnvoi =
    codeEnvoi === PROBLEM_CODES.logoTooLarge
      ? "Le fichier dépasse 512 Kio."
      : codeEnvoi === PROBLEM_CODES.logoUnsupportedType
        ? "Le contenu du fichier n'est pas une image PNG, JPEG ou WebP."
        : envoi.error !== null
          ? errorMessage(envoi.error)
          : null;

  return (
    <Card title="Logo" subtitle="PNG, JPEG ou WebP, 512 Kio au plus. Il remplace le sigle dans la navigation.">
      <div className={styles.formulaire}>
        <div className={styles.logoActuel}>
          <Logo src={company.logo_url} name={company.name} size="lg" />
          <span>{company.logo_url ? "Logo actuel" : "Aucun logo : le sigle est affiché"}</span>
        </div>
        <Field label="Nouveau logo" error={erreurFichier ?? erreurEnvoi ?? undefined}>
          {(commande) => (
            <FileInput
              id={commande.id}
              accept={LOGO_TYPES}
              maxBytes={LOGO_MAX_BYTES}
              value={fichier}
              onChange={(f, err) => {
                setFichier(f);
                setErreurFichier(err);
                envoi.reset();
              }}
              disabled={envoi.pending}
            />
          )}
        </Field>
        <div className={styles.boutons}>
          <Button variant="primary" onClick={() => void enregistrer()} loading={envoi.pending} disabled={fichier === null}>
            Enregistrer le logo
          </Button>
          {company.logo_url && (
            <Button variant="danger" onClick={() => setConfirmerRetrait(true)}>
              Supprimer le logo
            </Button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmerRetrait}
        title="Supprimer le logo ?"
        description="Le sigle de l'entreprise reprendra sa place dans la navigation."
        confirmLabel="Supprimer"
        tone="danger"
        pending={retrait.pending}
        onConfirm={() => void retirer()}
        onCancel={() => setConfirmerRetrait(false)}
      />
    </Card>
  );
}


function FormulaireAccent({ company, onUpdated }: MarqueFormulairesProps) {
  // Sans couleur propre, l'entreprise prend celle de la plateforme.
  const accentDefaut = useBranding().accent_color;
  const { notify } = useToast();
  const [couleur, setCouleur] = useState(company.accent_color ?? accentDefaut);
  useEffect(() => setCouleur(company.accent_color ?? accentDefaut), [company.accent_color, accentDefaut]);
  const maj = useApiMutation((c: string) => api.updateCompanyBranding(company.id, { accent_color: c }));

  const valide = HEX_COULEUR.test(couleur);
  const contraste = valide ? contrastRatio(couleur, "#ffffff") : 0;

  async function enregistrer(valeur: string) {
    const c = await maj.run(valeur);
    if (c === null) {
      return;
    }
    onUpdated(c);
    notify({ title: valeur === "" ? "Couleur par défaut rétablie" : "Couleur enregistrée", tone: "success" });
  }

  const erreur =
    problemCode(maj.error) === PROBLEM_CODES.invalidAccentColor || !valide
      ? "Indiquez une couleur au format #rrggbb."
      : maj.error !== null
        ? errorMessage(maj.error)
        : undefined;

  return (
    <Card title="Couleur d'accent" subtitle="Boutons, liens et repère de navigation. Les couleurs des seaux du grand livre ne changent jamais.">
      <div className={styles.formulaire}>
        <Field label="Couleur" error={erreur} hint="Format #rrggbb.">
          {(commande) => (
            <div className={styles.couleur}>
              <input
                type="color"
                aria-label="Choisir la couleur"
                value={valide ? couleur : accentDefaut}
                onChange={(e) => setCouleur(e.target.value)}
                disabled={maj.pending}
              />
              <input
                {...commande}
                type="text"
                value={couleur}
                onChange={(e) => setCouleur(e.target.value.trim().toLowerCase())}
                disabled={maj.pending}
              />
            </div>
          )}
        </Field>

        <div className={styles.apercu} style={accentOverride(valide ? couleur : accentDefaut)} aria-label="Aperçu de la couleur">
          <Button variant="primary">Bouton principal</Button>
          <a className={styles.apercuLien} href="#apercu" onClick={(e) => e.preventDefault()}>
            Un lien
          </a>
          <Badge tone="accent">Badge</Badge>
          <span className={styles.apercuNav}>Page courante</span>
        </div>
        {valide && (
          <p className={styles.contraste}>
            Contraste du blanc sur cette couleur : {contraste.toFixed(1)}:1.{" "}
            {contraste >= 4.5
              ? "Le texte des boutons restera blanc."
              : "Trop faible pour du texte blanc : le texte des boutons sera sombre sur cette couleur."}
          </p>
        )}

        <div className={styles.boutons}>
          <Button variant="primary" onClick={() => void enregistrer(couleur)} loading={maj.pending} disabled={!valide || couleur === (company.accent_color ?? "")}>
            Enregistrer la couleur
          </Button>
          {company.accent_color && (
            <Button variant="ghost" onClick={() => void enregistrer("")} loading={maj.pending}>
              Rétablir la couleur de la plateforme
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
